import { Trans, Translation } from 'react-i18next';
import cx from 'clsx';
import React from 'react';
import { FieldValues, Control } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { formatNetworth, formatNum } from '@/utils';
import style from './index.module.scss';
import { NumberInputField } from '../NumberInputField';
import { BadgeSelect } from '../BadgeSelect';
import { GroupSelect } from '../GroupSelect';

/**
 * 在数组中定义详细的 input，免得一个 字段 对应多个 input（区间、checkbox 多选等）
 */
export const InteractEligibilities = [
  {
    label: <Translation>{t => t('EligibilitiesFields_fields.1czgn0i')}</Translation>,
    name: 'min_net_worth',
    desc(v?: number) {
      return (
        <Trans
          i18nKey="EligibilitiesFields_fields.1czgn0i_desc"
          values={{ amount: formatNetworth(v) }}
        />
      );
    },
    // 不要用 =>，需要用到 this
    render(control: Control<FieldValues, object>, disabled?: boolean) {
      const { _formState } = control;
      return (
        <div className={style.inputContainer}>
          <Translation>
            {t => (
              <NumberInputField
                name={`${this.name}.1`}
                control={control}
                disabled={disabled}
                prefixStr="$"
                placeholder="0-50,000,000"
                rules={{
                  max: {
                    value: 50000000,
                    message: t('EligibilitiesFields_fields.16119bu'),
                  },
                  min: {
                    value: 0,
                    message: t('EligibilitiesFields_fields.1embtu4'),
                  },
                }}
              />
            )}
          </Translation>
          <ErrorMessage
            errors={_formState?.errors}
            name={`${this.name}.1`}
            render={({ message }) => {
              return <div className={style.errorMessage}>{message}</div>;
            }}
          />
        </div>
      );
    },
  },
  {
    label: <Translation>{t => t('EligibilitiesFields_fields.bp76ka')}</Translation>,
    name: 'min_age_days',
    desc(v?: number) {
      return (
        <Trans i18nKey="EligibilitiesFields_fields.bp76ka_desc" values={{ day: formatNum(v, 0) }} />
      );
    },
    render(control: Control<FieldValues, object>, disabled?: boolean) {
      const { _formState } = control;
      return (
        <div className={style.inputContainer}>
          <Translation>
            {t => (
              <NumberInputField
                name={`${this.name}.1`}
                control={control}
                disabled={disabled}
                suffixStr="days"
                inputWidth={109}
                placeholder="0-2000"
                rules={{
                  max: {
                    value: 2000,
                    message: t('EligibilitiesFields_fields.1kzsjt6'),
                  },
                  min: {
                    value: 0,
                    message: t('EligibilitiesFields_fields.p6jex0'),
                  },
                }}
              />
            )}
          </Translation>
          <ErrorMessage
            errors={_formState?.errors}
            name={`${this.name}.1`}
            render={({ message }) => {
              return <div className={style.errorMessage}>{message}</div>;
            }}
          />
        </div>
      );
    },
  },
  {
    label: <Translation>{t => t('EligibilitiesFields_fields.18wmc7d')}</Translation>,
    name: 'min_tvf',
    desc(v?: number) {
      return (
        <Trans
          i18nKey="EligibilitiesFields_fields.18wmc7d_desc"
          values={{ amount: formatNetworth(v) }}></Trans>
      );
    },
    render(control: Control<FieldValues, object>, disabled?: boolean) {
      const { _formState } = control;
      return (
        <div className={style.inputContainer}>
          <Translation>
            {t => (
              <NumberInputField
                name={`${this.name}.1`}
                control={control}
                disabled={disabled}
                prefixStr="$"
                placeholder="0-100,000,000"
                rules={{
                  max: {
                    value: 100000000,
                    message: t('EligibilitiesFields_fields.axn8yj'),
                  },
                  min: {
                    value: 0,
                    message: t('EligibilitiesFields_fields.1mevl77'),
                  },
                }}
              />
            )}
          </Translation>
          <ErrorMessage
            errors={_formState?.errors}
            name={`${this.name}.1`}
            render={({ message }) => {
              return <div className={style.errorMessage}>{message}</div>;
            }}
          />
        </div>
      );
    },
  },
  {
    label: <Translation>{t => t('EligibilitiesFields_fields.1u8vfp7')}</Translation>,
    name: 'min_follower_count',
    desc(v?: number) {
      return (
        <Trans
          i18nKey="EligibilitiesFields_fields.1u8vfp7_desc"
          values={{ amount: formatNum(v, 0) }}
        />
      );
    },
    render(control: Control<FieldValues, object>, disabled?: boolean) {
      const { _formState } = control;
      return (
        <div className={style.inputContainer}>
          <Translation>
            {t => (
              <NumberInputField
                name={`${this.name}.1`}
                control={control}
                disabled={disabled}
                placeholder="0-5000"
                rules={{
                  max: {
                    value: 5000,
                    message: t('EligibilitiesFields_fields.1ephtrc'),
                  },
                  min: {
                    value: 0,
                    message: t('EligibilitiesFields_fields.tovbcy'),
                  },
                }}
              />
            )}
          </Translation>
          <ErrorMessage
            errors={_formState?.errors}
            name={`${this.name}.1`}
            render={({ message }) => {
              return <div className={style.errorMessage}>{message}</div>;
            }}
          />
        </div>
      );
    },
  },
  {
    label: <Translation>{t => t('EligibilitiesFields_fields.1kms2up')}</Translation>,
    labelClassName: style.rankingLabel,
    name: 'min_ranking',
    desc(v?: number) {
      return (
        <Trans
          i18nKey="EligibilitiesFields_fields.1kms2up_desc"
          values={{ amount: formatNum(v, 0) }}
        />
      );
    },
    render(control: Control<FieldValues, object>, disabled?: boolean) {
      return (
        <div className={style.rankingInput}>
          <NumberInputField
            name={`${this.name}.1`}
            control={control}
            disabled={disabled}
            inputWidth={105}
          />
          <span className={style.rankingInputDesc}>
            <Translation>{t => t('EligibilitiesFields_fields.ecl5yz')}</Translation>
          </span>
        </div>
      );
    },
  },
  {
    label: <Translation>{t => t('EligibilitiesFields_fields.p9jkw2')}</Translation>,
    name: 'has_web3_id',
    desc() {
      return <Translation>{t => t('EligibilitiesFields_fields.p9jkw2')}</Translation>;
    },
  },
  {
    label: <Translation>{t => t('EligibilitiesFields_fields.11bel0b')}</Translation>,
    name: 'has_one_of_badges',
    desc(badgeName?: string) {
      return (
        <Trans i18nKey="EligibilitiesFields_fields.11bel0b_desc" values={{ name: badgeName }} />
      );
    },
    render(control: Control<FieldValues, object>, disabled?: boolean) {
      return (
        <div className={style.inputContainer}>
          <BadgeSelect name={`${this.name}.1`} control={control} disabled={disabled} />
        </div>
      );
    },
  },
  {
    label: <Translation>{t => t('EligibilitiesFields_fields.gk0ars')}</Translation>,
    name: 'is_group_member',
    labelContainerClassName: style.badgeLabel,
    desc(groupName?: string) {
      return (
        <Trans i18nKey="EligibilitiesFields_fields.gk0ars_desc" values={{ name: groupName }} />
      );
    },
    render(control: Control<FieldValues, object>, disabled?: boolean) {
      return (
        <div className={cx(style.inputContainer, style.groupSelectContainer)}>
          <GroupSelect name={`${this.name}.1`} control={control} disabled={disabled} width={170} />
        </div>
      );
    },
  },
  {
    label: <Translation>{t => t('EligibilitiesFields_fields.lhue9z')}</Translation>,
    name: 'has_followed_user',
    desc() {
      return <Translation>{t => t('EligibilitiesFields_fields.lhue9z_desc')}</Translation>;
    },
  },
];
